/**
 * Next-run preview for all schedule types — mirrors EventBridge / upcoming grid enumeration.
 */
import type { ScheduleType } from '@/types/schedule';

import type { DailyFormState, RepeatingFormState } from '../types/scheduleFormState';
import { type FireInstant, computeNextCronExecutions } from './recurringCronValidation';
import { buildRepeatingSchedulePayload } from './repeatingSchedulePayload';
import { computeDailyNextRunInstantFromTimeUtc } from './scheduleDateTime';
import { convertLocalTimeToUTC, getBrowserTimezone } from './timezoneData';
import { getStartDateForRepeatingScheduleAt } from './upcomingScheduleRuns';

export const SCHEDULE_PREVIEW_RUN_COUNT = 10;

const MS_PER_DAY = 86_400_000;

export interface SchedulePreviewRun {
  utc: Date;
  scheduleTimeZone: string;
  scheduleLabel: string;
  utcLabel: string;
  viewerLabel?: string;
  viewerTimeZone?: string;
}

export interface SchedulePreviewInput {
  scheduleType: ScheduleType;
  daily: DailyFormState;
  repeating: RepeatingFormState;
  cronExpression: string;
  /** Author / display timezone for labels (advanced cron, daily, repeating). */
  displayTimeZone: string;
  /** When true, repeating startingAt uses browser-local legacy conversion. */
  repeatingLegacyNoTimeZone?: boolean;
  after?: Date;
  refDate?: Date;
  count?: number;
  /** Browser timezone for optional "Your time" column; defaults to getBrowserTimezone(). */
  viewerTimeZone?: string;
}

/** Stable YYYY-MM-DD key for a UTC instant in the given timezone. */
export function getLocalDateKey(utc: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(utc);
}

/** Short DST-aware zone label for table headers (e.g. PDT, IST). */
export function formatPreviewTimeZoneShort(timeZone: string, refDate: Date = new Date()): string {
  if (timeZone === 'UTC') {
    return 'UTC';
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(refDate);

  return parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone;
}

/** Format a UTC instant for preview table cells (no "local" suffix). */
export function formatSchedulePreviewInstant(instant: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }).format(instant);
}

export function buildSchedulePreviewRun(
  utc: Date,
  scheduleTimeZone: string,
  viewerTimeZone: string
): SchedulePreviewRun {
  const run: SchedulePreviewRun = {
    utc,
    scheduleTimeZone,
    scheduleLabel: formatSchedulePreviewInstant(utc, scheduleTimeZone),
    utcLabel: formatSchedulePreviewInstant(utc, 'UTC'),
  };

  if (viewerTimeZone !== scheduleTimeZone) {
    run.viewerTimeZone = viewerTimeZone;
    run.viewerLabel = formatSchedulePreviewInstant(utc, viewerTimeZone);
  }

  return run;
}

function toPreviewRun(
  instant: FireInstant,
  scheduleTimeZone: string,
  viewerTimeZone: string
): SchedulePreviewRun {
  return buildSchedulePreviewRun(instant.utc, scheduleTimeZone, viewerTimeZone);
}

function computeDailyPreviewRuns(
  daily: DailyFormState,
  count: number,
  after: Date,
  refDate: Date,
  scheduleTimeZone: string,
  viewerTimeZone: string
): SchedulePreviewRun[] {
  const utc = convertLocalTimeToUTC(daily.hour, daily.minute, daily.timeZone, refDate);
  const timeUTC = `${String(utc.hour).padStart(2, '0')}:${String(utc.minute).padStart(2, '0')}`;

  const runs: SchedulePreviewRun[] = [];
  let slot = computeDailyNextRunInstantFromTimeUtc(timeUTC, after);

  while (slot && runs.length < count) {
    runs.push(
      toPreviewRun(
        {
          utc: slot,
          utcHour: slot.getUTCHours(),
          utcMinute: slot.getUTCMinutes(),
          localHour: daily.hour,
          localMinute: daily.minute,
        },
        scheduleTimeZone,
        viewerTimeZone
      )
    );
    slot = new Date(slot.getTime() + MS_PER_DAY);
  }

  return runs;
}

function repeatingIntervalMs(frequency: number, frequencyUnit: 'minute' | 'hour'): number {
  return frequencyUnit === 'hour' ? frequency * 3_600_000 : frequency * 60_000;
}

function computeRepeatingPreviewRuns(
  repeating: RepeatingFormState,
  count: number,
  after: Date,
  refDate: Date,
  scheduleTimeZone: string,
  viewerTimeZone: string,
  repeatingLegacyNoTimeZone?: boolean
): SchedulePreviewRun[] {
  const intervalMs = repeatingIntervalMs(repeating.frequency, repeating.frequencyUnit);
  if (intervalMs < 1) {
    return [];
  }

  const payload = buildRepeatingSchedulePayload(repeating, refDate, {
    legacyBrowserLocal: repeatingLegacyNoTimeZone,
    browserTimeZone: getBrowserTimezone(),
  });

  let anchor: Date;
  if (payload.startingAt) {
    const start = getStartDateForRepeatingScheduleAt(payload.startingAt, payload.timeZone, after);
    if (!start) {
      return [];
    }
    anchor = start;
  } else {
    anchor = new Date(after.getTime() + intervalMs);
  }

  while (anchor.getTime() <= after.getTime()) {
    anchor = new Date(anchor.getTime() + intervalMs);
  }

  const runs: SchedulePreviewRun[] = [];
  let slot = anchor;
  while (runs.length < count) {
    runs.push(
      toPreviewRun(
        {
          utc: slot,
          utcHour: slot.getUTCHours(),
          utcMinute: slot.getUTCMinutes(),
          localHour: repeating.startingAt?.hour ?? slot.getUTCHours(),
          localMinute: repeating.startingAt?.minute ?? slot.getUTCMinutes(),
        },
        scheduleTimeZone,
        viewerTimeZone
      )
    );
    slot = new Date(slot.getTime() + intervalMs);
  }

  return runs;
}

function computeCronPreviewRuns(
  cronExpression: string,
  count: number,
  after: Date,
  scheduleTimeZone: string,
  viewerTimeZone: string
): SchedulePreviewRun[] {
  const trimmed = cronExpression.trim();
  if (!trimmed) {
    return [];
  }

  return computeNextCronExecutions(trimmed, count, scheduleTimeZone, after).map((instant) =>
    toPreviewRun(instant, scheduleTimeZone, viewerTimeZone)
  );
}

/** Locked column order: Schedule | UTC | Your time (when viewer ≠ schedule). */
export function getSchedulePreviewColumnHeaders(
  scheduleTimeZone: string,
  viewerTimeZone: string,
  refDate: Date = new Date()
): string[] {
  const headers = [`Schedule (${formatPreviewTimeZoneShort(scheduleTimeZone, refDate)})`, 'UTC'];

  if (viewerTimeZone !== scheduleTimeZone) {
    headers.push(`Your time (${formatPreviewTimeZoneShort(viewerTimeZone, refDate)})`);
  }

  return headers;
}

/** Next N scheduled runs for create/edit preview — aligned with EventBridge / upcoming grid logic. */
export function computeSchedulePreviewRuns(input: SchedulePreviewInput): SchedulePreviewRun[] {
  const count = input.count ?? SCHEDULE_PREVIEW_RUN_COUNT;
  const after = input.after ?? new Date();
  const refDate = input.refDate ?? after;
  const scheduleTimeZone = input.displayTimeZone.trim() || getBrowserTimezone();
  const viewerTimeZone = input.viewerTimeZone?.trim() || getBrowserTimezone();

  switch (input.scheduleType) {
    case 'daily':
      return computeDailyPreviewRuns(
        input.daily,
        count,
        after,
        refDate,
        scheduleTimeZone,
        viewerTimeZone
      );
    case 'repeating':
      return computeRepeatingPreviewRuns(
        input.repeating,
        count,
        after,
        refDate,
        scheduleTimeZone,
        viewerTimeZone,
        input.repeatingLegacyNoTimeZone
      );
    case 'cron':
      return computeCronPreviewRuns(
        input.cronExpression,
        count,
        after,
        scheduleTimeZone,
        viewerTimeZone
      );
    default:
      return [];
  }
}
